// src/edu-world/systems/soft-guide.ts
// Gentle navigation suggestion system — shows sparkle indicator toward Rosa
// if the player hasn't interacted with anything for a while.
// Present as optional visual suggestion only — no blocking prompts.
// Requirements: 25.1, 25.2, 25.3, 25.4

import Phaser from 'phaser';
import type { ZoneTransitionSystem } from './zone-transition';

/**
 * SoftGuide provides gentle visual navigation hints when the player
 * seems idle. It points toward a target NPC (Rosa) using a pulsing
 * sparkle/arrow that fades when the player begins moving.
 *
 * Constraints:
 * - Activates only after 20 seconds of no interaction
 * - Does NOT activate if player interacted within last 10 seconds
 * - Fades within 2 seconds when player begins moving
 * - Max 1 suggestion per 30 seconds (cooldown)
 * - Purely visual — never blocks or interrupts
 * - Does NOT guide toward placeholder zones (Requirement 1.10)
 */
export class SoftGuide {
  private scene: Phaser.Scene;
  private getPlayerPosition: () => { x: number; y: number };
  private targetPosition: { x: number; y: number };
  private zoneTransition: ZoneTransitionSystem | null;

  private lastInteractionTime: number = Date.now();
  private lastSuggestionTime: number = 0;
  private lastPlayerPosition: { x: number; y: number } = { x: 0, y: 0 };

  private indicatorContainer: Phaser.GameObjects.Container | null = null;
  private isShowingIndicator: boolean = false;
  private isFading: boolean = false;

  // Configuration
  private readonly IDLE_THRESHOLD_MS = 20000; // 20 seconds
  private readonly RECENT_INTERACTION_MS = 10000; // 10 seconds
  private readonly COOLDOWN_MS = 30000; // 30 seconds
  private readonly FADE_DURATION_MS = 2000; // 2 seconds
  private readonly MOVEMENT_THRESHOLD = 2; // pixels of movement to trigger fade

  constructor(
    scene: Phaser.Scene,
    getPlayerPosition: () => { x: number; y: number },
    targetPosition: { x: number; y: number },
    zoneTransition?: ZoneTransitionSystem | null
  ) {
    this.scene = scene;
    this.getPlayerPosition = getPlayerPosition;
    this.targetPosition = targetPosition;
    this.zoneTransition = zoneTransition ?? null;
    this.lastPlayerPosition = getPlayerPosition();
  }

  /**
   * Call this whenever the player interacts with anything:
   * NPC activation, micro-interaction, mission accept/decline, etc.
   */
  recordInteraction(): void {
    this.lastInteractionTime = Date.now();

    // If indicator is showing, fade it immediately
    if (this.isShowingIndicator && !this.isFading) {
      this.fadeIndicator();
    }
  }

  /**
   * Update loop — should be called on each scene update frame.
   * Checks idle time, movement, and decides whether to show/fade the guide.
   */
  update(): void {
    const now = Date.now();
    const currentPos = this.getPlayerPosition();

    // Check if player is moving
    const dx = currentPos.x - this.lastPlayerPosition.x;
    const dy = currentPos.y - this.lastPlayerPosition.y;
    const moved = Math.sqrt(dx * dx + dy * dy) > this.MOVEMENT_THRESHOLD;

    // If indicator is showing and player begins moving → fade it
    if (this.isShowingIndicator && moved && !this.isFading) {
      this.fadeIndicator();
    }

    // Update stored player position
    this.lastPlayerPosition = { x: currentPos.x, y: currentPos.y };

    // Don't show indicator if already showing or fading
    if (this.isShowingIndicator || this.isFading) {
      return;
    }

    // Check cooldown (max 1 suggestion per 30s)
    if (now - this.lastSuggestionTime < this.COOLDOWN_MS) {
      return;
    }

    // Don't activate if player interacted recently (within 10s)
    if (now - this.lastInteractionTime < this.RECENT_INTERACTION_MS) {
      return;
    }

    // Check if player has been idle for 20 seconds
    const timeSinceInteraction = now - this.lastInteractionTime;
    if (timeSinceInteraction >= this.IDLE_THRESHOLD_MS) {
      // Requirement 1.10: Don't guide toward placeholder zones
      if (this.zoneTransition && this.zoneTransition.isPositionInPlaceholderZone(
        this.targetPosition.x, this.targetPosition.y
      )) {
        return;
      }
      this.showIndicator();
    }
  }

  /**
   * Shows the gentle sparkle indicator pointing from player toward target.
   */
  private showIndicator(): void {
    const playerPos = this.getPlayerPosition();

    // Record that we showed a suggestion
    this.lastSuggestionTime = Date.now();
    this.isShowingIndicator = true;

    // Calculate direction from player to target
    const dx = this.targetPosition.x - playerPos.x;
    const dy = this.targetPosition.y - playerPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // If player is already very close to target, don't guide
    if (distance < 60) {
      this.isShowingIndicator = false;
      return;
    }

    // Place the arrow indicator 60px from the player in the direction of the target
    const normX = dx / distance;
    const normY = dy / distance;
    const indicatorX = playerPos.x + normX * 60;
    const indicatorY = playerPos.y + normY * 60;

    // Calculate rotation angle (degrees) for the arrow emoji
    const angleDeg = Phaser.Math.RadToDeg(Math.atan2(dy, dx));

    // Create indicator container
    this.indicatorContainer = this.scene.add.container(indicatorX, indicatorY);
    this.indicatorContainer.setDepth(20);
    this.indicatorContainer.setAlpha(0);

    // ── Clear arrow emoji pointing toward Rosa ──
    const arrowText = this.scene.add.text(0, 0, '👉', {
      fontSize: '28px',
    }).setOrigin(0.5);
    arrowText.setAngle(angleDeg);

    // ── Soft glow behind arrow ──
    const glow = this.scene.add.circle(0, 0, 20, 0xfff9c4, 0.3);

    this.indicatorContainer.add([glow, arrowText]);

    // ── Fade in ──
    this.scene.tweens.add({
      targets: this.indicatorContainer,
      alpha: 1,
      duration: 500,
      ease: 'Sine.easeOut',
    });

    // ── Gentle pulse animation ──
    this.scene.tweens.add({
      targets: this.indicatorContainer,
      scale: { from: 0.9, to: 1.1 },
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // ── Auto-fade after 5 seconds even if player doesn't move ──
    this.scene.time.delayedCall(5000, () => {
      if (this.isShowingIndicator && !this.isFading) {
        this.fadeIndicator();
      }
    });
  }

  /**
   * Fades the indicator within 2 seconds.
   */
  private fadeIndicator(): void {
    if (!this.indicatorContainer || this.isFading) {
      return;
    }

    this.isFading = true;

    this.scene.tweens.add({
      targets: this.indicatorContainer,
      alpha: 0,
      duration: this.FADE_DURATION_MS,
      ease: 'Sine.easeIn',
      onComplete: () => {
        this.destroyIndicator();
      },
    });
  }

  /**
   * Immediately destroys the indicator and resets state.
   */
  private destroyIndicator(): void {
    if (this.indicatorContainer) {
      this.indicatorContainer.destroy(true);
      this.indicatorContainer = null;
    }
    this.isShowingIndicator = false;
    this.isFading = false;
  }

  /**
   * Update the target position (e.g., if NPC moves or target changes).
   */
  setTargetPosition(position: { x: number; y: number }): void {
    this.targetPosition = position;
  }

  /**
   * Returns whether the indicator is currently showing.
   */
  getIsShowing(): boolean {
    return this.isShowingIndicator;
  }

  /**
   * Returns the timestamp of the last recorded interaction.
   */
  getLastInteractionTime(): number {
    return this.lastInteractionTime;
  }

  /**
   * Clean up all resources.
   */
  destroy(): void {
    this.destroyIndicator();
  }
}
